import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth-context";
import { Pill } from "../components/Pill";
import * as shared from "@ventrin/shared";
type ActivePlan = "early_access" | "starter" | "team" | "enterprise";

function hasFalseFlagReview(ws: { plan?: any } | undefined): boolean {
  const plan: ActivePlan = shared.resolvePlan(ws?.plan);
  return shared.PLANS[plan].features.includes("false_flag_review");
}

const FILTERS: Array<{ key: "pending" | "reviewed_false" | "reviewed_valid" | "all"; label: string }> = [
  { key: "pending",         label: "Pending" },
  { key: "reviewed_false",  label: "False positives" },
  { key: "reviewed_valid",  label: "Upheld" },
  { key: "all",             label: "All" },
];

export function FalseFlagsPage() {
  const { profile } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [filter, setFilter] = useState<typeof FILTERS[number]["key"]>("pending");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasEntitlement = hasFalseFlagReview(profile.workspace);
  const currentPlan = shared.resolvePlan(profile.workspace?.plan);

  async function load() {
    if (!hasEntitlement) return;
    setError(null);
    try {
      const res = await api.listFalseFlags({ status: filter === "all" ? undefined : filter });
      setReports(res.reports);
    } catch (e: any) {
      const code = e?.details?.code ?? e?.code;
      if (code === "plan-upgrade-required") {
        setError("False-flag review is a Team+ feature. Upgrade in the Billing tab.");
      } else {
        setError(e?.message ?? "Could not load reports.");
      }
    }
  }
  useEffect(() => { load(); }, [filter, hasEntitlement]);

  async function resolve(id: string, decision: "false_positive" | "valid", resolutionNote?: string) {
    setBusy(true);
    try {
      await api.resolveFalseFlag({ reportId: id, decision, resolutionNote });
      await load();
    } finally { setBusy(false); }
  }

  const pendingCount = useMemo(() => reports.filter((r) => r.status === "pending").length, [reports]);

  if (!hasEntitlement) {
    return (
      <>
        <header className="sg-page-header">
          <div>
            <h1 className="sg-page-title">False flags</h1>
            <div className="sg-page-sub">Review user-reported policy disputes.</div>
          </div>
        </header>
        <div className="sg-panel" style={{ borderLeft: "3px solid var(--vt-amber, #b3791a)" }}>
          <div className="sg-panel-pad sg-stack">
            <strong>This is a Team+ feature.</strong>
            <div>
              You're on the {currentPlan} plan. Upgrade in the{" "}
              <a href="#/billing">Billing tab</a> to let your users flag false positives and review them here.
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="sg-page-header">
        <div>
          <h1 className="sg-page-title">False flags</h1>
          <div className="sg-page-sub">User-submitted reports. Resolve to keep your policies sharp.</div>
        </div>
        <div className="sg-row" style={{ gap: 10 }}>
          {pendingCount > 0 && <Pill kind="warn">{pendingCount} pending</Pill>}
        </div>
      </header>

      {error && (
        <div className="sg-panel" style={{ marginBottom: 12, borderLeft: "3px solid var(--vt-red, #b3261a)" }}>
          <div className="sg-panel-pad">{error}</div>
        </div>
      )}

      <div className="sg-filters">
        {FILTERS.map((s) => (
          <button key={s.key} className={`sg-chip ${filter === s.key ? "active" : ""}`} onClick={() => setFilter(s.key)}>
            {s.label}
          </button>
        ))}
      </div>

      {reports.length === 0 && <div className="sg-empty">No reports in this view.</div>}
      <div className="sg-stack">
        {reports.map((r) => (
          <div key={r.id} className="sg-card" style={{ gridTemplateColumns: "1fr auto" }}>
            <div className="sg-card-body">
              <div className="sg-card-title">{r.note || "No note provided."}</div>
              <div className="sg-muted" style={{ fontSize: 12 }}>
                user {r.userId?.slice(0, 6)}… · {new Date(r.createdAt).toLocaleString()}
                {r.resolutionNote ? ` · ${r.resolutionNote}` : ""}
              </div>
            </div>
            <div className="sg-card-actions">
              <Pill kind={r.status === "pending" ? "warn" : r.status === "reviewed_false" ? "rewrite" : "block"}>
                {r.status === "pending" ? "Pending" : r.status === "reviewed_false" ? "False positive" : "Upheld"}
              </Pill>
              {r.status === "pending" && (
                <>
                  <button className="sg-btn sg-btn-sm" onClick={() => resolve(r.id, "false_positive")} disabled={busy}>Mark false positive</button>
                  <button className="sg-btn sg-btn-sm sg-btn-danger" onClick={() => resolve(r.id, "valid")} disabled={busy}>Uphold rule</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
